import { DateType, DateValueType } from 'react-tailwindcss-datepicker';

// Formats date and time to local format Ex: Wed 19 November, 2025 at 12:38:38 pm
export const formatDateTime = (
  dateInput?: string | number | Date,
  locale: string = 'en-IN'
): string => {
  if (!dateInput) return '';

  // Django returns timestamps with microseconds (6 digits).
  // JavaScript's Date() only supports milliseconds (3 digits),
  // so strip extra digits to avoid "Invalid Date".
  if (typeof dateInput === 'string') {
    dateInput = dateInput.replace(/(\.\d{3})\d+(Z)?$/, '$1$2'); // strips extra digits
  }

  const date = new Date(dateInput);

  if (isNaN(date.getTime())) {
    console.warn('Invalid date received:', dateInput);
    return '';
  }

  return date.toLocaleString(locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// Formats a date to YYYY-MM-DD, e.g. 2026-07-02
export const formatDateYMD = (dateInput?: string | number | Date): string => {
  if (!dateInput) return '';

  // Django returns timestamps with microseconds (6 digits).
  // JavaScript's Date() only supports milliseconds (3 digits),
  // so strip extra digits to avoid "Invalid Date".
  if (typeof dateInput === 'string') {
    dateInput = dateInput.replace(/(\.\d{3})\d+(Z)?$/, '$1$2'); // strips extra digits
  }

  const date = new Date(dateInput);

  if (isNaN(date.getTime())) {
    console.warn('Invalid date received:', dateInput);
    return '';
  }

  return date.toISOString().split('T')[0];
};

export const formatDateRange = (dateRange: DateValueType) => {
  let startDate: DateType = dateRange?.startDate || null;
  let endDate: DateType = dateRange?.endDate || null;

  if (typeof dateRange?.startDate === 'string') {
    const temp = dateRange.startDate.split('-').map((val) => parseInt(val));
    startDate = new Date(temp[0], temp[1] - 1, temp[2]);
  }

  if (typeof dateRange?.endDate === 'string') {
    const temp = dateRange.endDate.split('-').map((val) => parseInt(val));
    endDate = new Date(temp[0], temp[1] - 1, temp[2]);
  }

  return {
    startDate,
    endDate,
  };
};

export const sortFuncStrings = (
  a: string,
  b: string,
  dir: 'asc' | 'desc' = 'asc'
) => {
  if (a.toLowerCase() < b.toLowerCase()) {
    return dir === 'asc' ? -1 : 1;
  }
  if (a.toLowerCase() > b.toLowerCase()) {
    return dir === 'asc' ? 1 : -1;
  }
  return 0;
};

export const sortFuncDropdownOptions = <T extends string | { label: string }>(
  optA: T,
  optB: T,
  dir: 'asc' | 'desc' = 'asc'
) => {
  const a = typeof optA === 'string' ? optA : optA.label;
  const b = typeof optB === 'string' ? optB : optB.label;

  if (a.toLowerCase() < b.toLowerCase()) {
    return dir === 'asc' ? -1 : 1;
  }
  if (a.toLowerCase() > b.toLowerCase()) {
    return dir === 'asc' ? 1 : -1;
  }
  return 0;
};

export const toFixedNum = (num: number, fixedPlaces = 0) => {
  return num.toLocaleString(undefined, {
    minimumFractionDigits: fixedPlaces,
    maximumFractionDigits: fixedPlaces,
  });
};

export const shortenNumberStr = (value: number) => {
  return value >= 1_000_000_000
    ? `${(value / 1_000_000_000).toFixed(1)}B`
    : value >= 1_000_000
      ? `${(value / 1_000_000).toFixed(1)}M`
      : value >= 1_000
        ? `${(value / 1_000).toFixed(1)}K`
        : value.toString();
};

export const mockRequest = <T>(
  result: 'success' | 'failure',
  resData: T,
  delay: number = 1500
) =>
  new Promise<T>((res, rej) => {
    setTimeout(() => {
      if (result === 'success') {
        res(resData);
      } else {
        rej(resData);
      }
    }, delay);
  });

export const randomId = (str: string) => {
  const alphaNumeric = Math.random().toString(36).slice(2);
  return `${str}__${alphaNumeric}`;
};

export const sanitizeAcctNumPaste = (numStr: string) => {
  const formatted = numStr
    .trim()
    .replace(/[^0-9-]/g, '')
    .replace(/--+/g, '-');
  return formatted;
};
export const sanitizeAcctNumInput = (
  newValue: string,
  evt?: React.ChangeEvent<HTMLInputElement>,
  priorValue: string = ''
) => {
  const newLength = newValue.length;
  if (!newLength || newLength < priorValue.length) return newValue;

  const cusorStartIndex = (evt?.currentTarget.selectionStart || newLength) - 1;

  const sanitizedStart = newValue.slice(0, cusorStartIndex);
  const sanitizedChar = newValue[cusorStartIndex].replace(/[^0-9-]/g, '');
  const sanitizedEnd =
    cusorStartIndex + 1 < newLength ? newValue.slice(cusorStartIndex + 1) : '';

  // No consecutive hyphens
  if (
    (sanitizedStart.slice(-1) === '-' || sanitizedEnd[0] === '-') &&
    sanitizedChar === '-'
  ) {
    return sanitizedStart + sanitizedEnd;
  }

  return sanitizedStart + sanitizedChar + sanitizedEnd;
};

// PREVENT ENTER KEY FROM SUBMITTING FORM
export const onPreventEnterKeySubmit = (evt: React.KeyboardEvent) => {
  if (evt.key === 'Enter') {
    evt.preventDefault();
  }
};

// Checks that a file name includes every given component (e.g. client, line of
// business, market), ignoring case and treating spaces/hyphens/underscores as equivalent.
export const fileNameHasAllComponents = (
  fileName: string,
  components: string[]
): boolean => {
  const nameWithoutExt = fileName.replace(/\.[^./\\]+$/, '');
  const normalizedFileName = ` ${nameWithoutExt.replace(/[ \-_]+/g, ' ').toLowerCase()} `;
  return components.every((component) => {
    const normalizedComponent = component
      .trim()
      .replace(/[ \-_]+/g, ' ')
      .toLowerCase()
      .trim();
    return normalizedFileName.includes(` ${normalizedComponent} `);
  });
};

interface ValidateConfigFileNameParams {
  client: string;
  lineOfBusiness: string;
  market: string;
  moduleWords: string[];
  accountName: string | null;
  exampleFileName: string;
}

// Validates a selected config-upload file's name against the account name +
// module words (e.g. ['auto', 'optimize']), shared by the SettingsComp upload buttons.
export const validateConfigFileName = (
  file: File,
  {
    client,
    lineOfBusiness,
    market,
    moduleWords,
    accountName,
    exampleFileName,
  }: ValidateConfigFileNameParams
): { isValid: boolean; error?: string } => {
  if (!accountName || !client) {
    return { isValid: true };
  }

  const validComponents = [
    client,
    ...(lineOfBusiness ? [lineOfBusiness] : []),
    market,
    ...moduleWords,
  ];

  if (fileNameHasAllComponents(file.name, validComponents)) {
    return { isValid: true };
  }

  return {
    isValid: false,
    error: `File name must include the OneSearch Account Name "${accountName}". \nExample: ${exampleFileName}`,
  };
};

export const isValidDomain = (val: string) => {
  const trimmed = val.trim();
  if (trimmed.endsWith('.')) return false;

  const regex =
    /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9](?:\/[^\s]*)?\/?$/i;

  return regex.test(trimmed);
};

/**
 * Formats a currency symbol with appropriate spacing.
 * Adds a space after currency codes (length > 1) for better readability.
 *
 * @param currencySymbol - The currency symbol or code (e.g., '$', 'USD', '€')
 * @param defaultSymbol - The default symbol to use if currencySymbol is null/undefined (defaults to '$')
 * @returns The formatted currency symbol with space if needed
 *
 * @example
 * formatCurrencySymbol('$') // returns '$'
 * formatCurrencySymbol('USD') // returns 'USD '
 * formatCurrencySymbol(null) // returns '$'
 * formatCurrencySymbol(undefined, '€') // returns '€'
 */
export const formatCurrencySymbol = (
  currencySymbol: string | null | undefined,
  defaultSymbol: string = '$'
): string => {
  if (!currencySymbol) {
    return defaultSymbol;
  }
  // Add space if the symbol is a currency code (length > 1) instead of a symbol
  return currencySymbol.length > 1 ? `${currencySymbol} ` : currencySymbol;
};
